/**
 * Regras de negócio do colchão.
 * Mantidas puras (sem dependência de storage/UI) para testabilidade.
 */
export function computeTotalHeightMm(layers) {
    return layers
        .filter((l) => l.visible !== false)
        .reduce((sum, l) => sum + l.espessuraMm, 0);
}
/**
 * Ordena camadas conforme papel construtivo: core na base, pillow top no topo.
 * Dentro do mesmo papel, respeita ordem manual.
 */
const ROLE_ORDER = {
    core: 0,
    transition: 1,
    comfort: 2,
    'top-comfort': 3,
    'euro-top': 4,
    'pillow-top': 5,
};
export function sortLayersByRole(layers) {
    return [...layers]
        .sort((a, b) => {
        const roleDiff = ROLE_ORDER[a.role] - ROLE_ORDER[b.role];
        return roleDiff !== 0 ? roleDiff : a.ordem - b.ordem;
    })
        .map((l, i) => ({ ...l, ordem: i }));
}
/**
 * Resseta a ordem de acordo com a posição atual no array (após drag & drop).
 */
export function reindexLayers(layers) {
    return layers.map((l, i) => ({ ...l, ordem: i }));
}
/**
 * Valida uma camada contra seu material de referência.
 * Retorna issues — não joga.
 */
export function validateLayer(layer, material) {
    const issues = [];
    if (!material) {
        issues.push({
            layerId: layer.id,
            severity: 'error',
            message: `Material "${layer.materialId}" não encontrado no catálogo.`,
        });
        return issues;
    }
    if (layer.espessuraMm < material.alturaMinMm) {
        issues.push({
            layerId: layer.id,
            severity: 'error',
            message: `Espessura abaixo do mínimo (${material.alturaMinMm} mm) para ${material.nome}.`,
        });
    }
    if (layer.espessuraMm > material.alturaMaxMm) {
        issues.push({
            layerId: layer.id,
            severity: 'error',
            message: `Espessura acima do máximo (${material.alturaMaxMm} mm) para ${material.nome}.`,
        });
    }
    return issues;
}
/** Regras estruturais no conjunto de camadas */
export function validateLayerStack(layers, getMaterial) {
    const issues = [];
    for (const l of layers) {
        issues.push(...validateLayer(l, getMaterial(l.materialId)));
    }
    const cores = layers.filter((l) => l.role === 'core');
    if (cores.length === 0 && layers.length > 0) {
        issues.push({
            layerId: '__stack__',
            severity: 'warning',
            message: 'Nenhuma camada de núcleo/sustentação definida.',
        });
    }
    if (cores.length > 1) {
        issues.push({
            layerId: '__stack__',
            severity: 'warning',
            message: 'Mais de uma camada de núcleo presente — revise a construção.',
        });
    }
    const pillows = layers.filter((l) => l.role === 'pillow-top');
    const euros = layers.filter((l) => l.role === 'euro-top');
    if (pillows.length > 0 && euros.length > 0) {
        issues.push({
            layerId: '__stack__',
            severity: 'warning',
            message: 'Pillow top e Euro top não são tipicamente combinados.',
        });
    }
    const totalHeight = computeTotalHeightMm(layers);
    if (totalHeight > 500) {
        issues.push({
            layerId: '__stack__',
            severity: 'warning',
            message: `Altura total de ${(totalHeight / 10).toFixed(1)} cm é incomum para colchões residenciais.`,
        });
    }
    return issues;
}
/** Checa se um projeto está em estado consistente para render */
export function isProjectRenderable(project) {
    return (project.listaDeCamadas.length > 0 &&
        project.dimensoes.larguraMm > 0 &&
        project.dimensoes.comprimentoMm > 0);
}
